import { type Prompt } from "@1js/pl-types";
import type * as React from "react";
import { useCallback } from "react";

export const useHandleKeyDown = () => {
  type HandleKeyDownOptions = {
    displayedPrompts: Prompt[];
    onPressEnterOrSpace: (prompt: Prompt, displayPosition?: number) => void;
  };

  const handleArrowKeyNavigation = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const currentCard = target.closest('[data-type="card"]') as HTMLElement;

      if (!currentCard) return;

      const parentElement = currentCard.parentElement;

      if (!parentElement) return;

      let newTarget: HTMLElement | undefined;

      switch (event.key) {
        case "ArrowUp":
          newTarget = parentElement.previousElementSibling
            ?.firstElementChild as HTMLElement;
          break;
        case "ArrowDown":
          newTarget = parentElement.nextElementSibling
            ?.firstElementChild as HTMLElement;
          break;
        default:
          return;
      }

      if (newTarget) {
        event.preventDefault();
        (newTarget as HTMLElement).focus();
        (newTarget as HTMLElement).tabIndex = 0;
        (currentCard as HTMLElement).tabIndex = -1;
      }
    },
    []
  );

  // This function is made generic so it can be used in pl-card-components and pl-web-components
  // The promptsSource and onPressEnterOrSpace are passed as arguments to handle differences in implementation
  const createHandleKeyDown = useCallback(
    ({ displayedPrompts, onPressEnterOrSpace }: HandleKeyDownOptions) => {
      return (event: React.KeyboardEvent<HTMLDivElement>) => {
        handleArrowKeyNavigation(event);

        const target = event.target as HTMLElement;
        const isCard = target.getAttribute("data-type") === "card";

        if (isCard && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();

          const promptId = target.getAttribute("data-prompt-id");
          const displayPositionStr = target.getAttribute(
            "data-display-position"
          );
          const displayPosition = displayPositionStr
            ? parseInt(displayPositionStr, 10)
            : undefined;

          const prompt = displayedPrompts.find((p) => p.Id === promptId);

          if (prompt) {
            onPressEnterOrSpace(prompt, displayPosition);
          }
        }
      };
    },
    [handleArrowKeyNavigation]
  );

  return {
    createHandleKeyDown,
  };
};
